import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient, HttpEvent } from '@angular/common/http';
import {
    CacheResponse, LastClickedSymbol, SetCacheRequestParams,
    SetSymbolCacheRequestInterface, DefaultFormSettings, NotificationSettings,
    SelectedView, SetSelectedView
} from '../../../app.interfaces';

import 'rxjs/add/operator/map';
import { of, Subscription, Observable } from 'rxjs';
import { PersonalizeView, DefaultmarketWatch } from 'src/app/app.structs';
import { ConfigParams, BrokerDetails, MarketWatchSettings, SortStateInterface } from 'sharedweb/app.interface';
import { CACHE_KEYS, DEFAULT_PINNED_SYMBOLS } from 'src/app/_helpers';
import { resolve } from 'url';
import { applicationUrls } from 'sharedweb/helpers/urls';
import { CACHE__KEYS, DEFAULT_PINNED_SYMBOLS_OBJ } from 'sharedweb/helpers/shared-constants';

@Injectable()
export class GenericRequestsService implements OnDestroy {

    private _lastClickedSymbol: LastClickedSymbol;
    sortingData: SortStateInterface;
    private _sortingDataRequest: Promise<SortStateInterface>;
    private _lastClickedSymbolRequest: Promise<LastClickedSymbol>;
    private _orderFormSettings: DefaultFormSettings;
    private _orderFormSettingsRequest: Promise<DefaultFormSettings>;
    private _notificationSettings: NotificationSettings = {
        notifications: true,
        orderConfirmations: true,
        alertNotifications: true,
        broadcastNotifications: true
    };
    private _notificationSettingsRequest: Promise<NotificationSettings>;
    private _selectedView: SelectedView;
    private _chartLibrary: string;
    private _marketWatchSettings: MarketWatchSettings;
    private _marketWatchSettingsRequest: Promise<MarketWatchSettings>;
    private _subscriptions: Subscription = new Subscription();
    private _configParams: ConfigParams;
    private _configParamsRequest: Promise<ConfigParams>;
    private _brokerDetails: BrokerDetails;
    private _brokerDetailsRequest: Promise<BrokerDetails>;
    private _defaultMarketWatch: DefaultmarketWatch;
    private _defaultMarketWatchRequest: Promise<DefaultmarketWatch>;
    private _currentTheme: string;
    private _currentTable = new Map<String, any>();
    private _currentTableRequest = new Map<String, Promise<any>>();
    private _UPIId: string;
    constructor(
        private _http: HttpClient
    ) {
    }

    ngOnDestroy() {
        this._subscriptions.unsubscribe();
    }

    /*
        Generic Service for cache from webservices
    */

    getLastClickedSymbol(): Promise<LastClickedSymbol> { 
        if (this._lastClickedSymbol === undefined) {
            if (this._lastClickedSymbolRequest) {
                return this._lastClickedSymbolRequest;
            } else {
                const url = applicationUrls.getCacheUrl;
                this._lastClickedSymbolRequest = new Promise((resolve, reject) => {
                    this._http.get<CacheResponse>(url, {
                        params: {
                            key: CACHE_KEYS.lastClickedSymbol
                        }
                    }).subscribe(res => {
                        // Receiving Json sting with single quotes
                        if (res.data) {
                            this._lastClickedSymbol = JSON.parse(res.data);
                        } else {
                            this._lastClickedSymbol = null;
                        }
                        resolve(this._lastClickedSymbol);
                    }, (err) => {
                        reject(err);
                    });
                });
                return this._lastClickedSymbolRequest;
            }
        } else {
            return new Promise(resolve => resolve(this._lastClickedSymbol));
        }
    }

    setLastClickedSymbol(symbol: SetSymbolCacheRequestInterface) {
        const url = applicationUrls.setCacheUrl;
        this._lastClickedSymbol = symbol.data;
        return this._http.post(url, {
            key: CACHE_KEYS.lastClickedSymbol,
            data: JSON.stringify(symbol.data)
        });
    }


    setSortingData(data: SortStateInterface) {
        const url = applicationUrls.setCacheUrl;
        try {
            return this._http.post(url, {
                key: CACHE_KEYS.SortingData,
                data: JSON.stringify(data)
            });
        }
        catch (e) {
            console.log("error", e);
        }

    }

    getSortingData() {
      if (!this.sortingData) {
                const url = applicationUrls.getCacheUrl;
                this._sortingDataRequest = new Promise((resolve, reject) => {
                    this._http.get<CacheResponse>(url, {
                        params: {
                            key: CACHE_KEYS.SortingData
                        }
                    }).subscribe(res => {
                        // Receiving Json sting with single quotes
                        if (res.data) {
                            this.sortingData = JSON.parse(res.data);
                        } else {
                            this.sortingData = null;
                        }
                        resolve(this.sortingData);
                    }, (err) => {
                        reject(err);
                    });
                });
                return this._sortingDataRequest;
            
        } else {
            return new Promise(resolve => resolve(this.sortingData));
        }
    }

    getWidgetState() {
        const url = applicationUrls.getCacheUrl;
        return this._http.get<CacheResponse>(url, {
            params: {
                key: CACHE_KEYS.widget
            }
        })

    }

    setWidgetState(widgetState) {
        const url = applicationUrls.setCacheUrl;
        const data = {
            key: CACHE_KEYS.widget,
            data: JSON.stringify(widgetState)
        };
        return this._http.post(url, data);
    }


    getDefaultFormSettings(): Promise<DefaultFormSettings> {
        if (this._orderFormSettings === undefined) {
            if (this._orderFormSettingsRequest) {
                return this._orderFormSettingsRequest;
            } else {
                const url = applicationUrls.getCacheUrl;
                this._orderFormSettingsRequest = new Promise((resolve, reject) => {
                    this._http.get<CacheResponse>(url, {
                        params: {
                            key: CACHE_KEYS.defaultFormSettings
                        }
                    }).subscribe(res => {
                        if (res.data) {
                            this._orderFormSettings = JSON.parse(res.data);
                        } else {
                            this._orderFormSettings = null;
                        }
                        resolve(this._orderFormSettings);
                    }, (err) => {
                        reject(err);
                    });
                });
                return this._orderFormSettingsRequest;
            }
        } else {
            return new Promise(resolve => resolve(this._orderFormSettings));
        }
    }

    setDefaultOrderSettings(setDefaultOrderSettingsRequest: SetCacheRequestParams) {
        const url = applicationUrls.setCacheUrl;
        this._orderFormSettings = JSON.parse(setDefaultOrderSettingsRequest.data);
        return this._http.post(url, setDefaultOrderSettingsRequest);
    }

    // Layout settings are not being saved currently but there's a dependency on get value.
    // So, for now returning default values, to not break code.
    getSelectedView(): Promise<SelectedView> {
        return Promise.resolve({ selectedView: PersonalizeView.pro });
    }


    setSelectedView(setSelectedViewRequest: SelectedView) {
        const url = applicationUrls.setCacheUrl;
        this._selectedView = setSelectedViewRequest;
        const data: SetSelectedView = {
            key: CACHE_KEYS.selectedLayout,
            data: JSON.stringify(setSelectedViewRequest)
        };
        return this._http.post(url, data);
    }

    // Notifications settings are not being saved currently but there's a dependency on get value.
    // So, for now returning default values, to not break code.
    getNotificationSettings(): Promise<NotificationSettings> {
        return Promise.resolve(this._notificationSettings);
    }

    // Commented for now as might be needed in future.
    // getNotificationSettings(): Promise<NotificationSettings> {
    //     if (this._notificationSettings === undefined) {
    //         if (this._notificationSettingsRequest) {
    //             return this._notificationSettingsRequest;
    //         } else {
    //             const url = applicationUrls.getCacheUrl;
    //             this._notificationSettingsRequest = new Promise((resolve, reject) => {
    //                 this._http.get<CacheResponse>(url, {
    //                     params: {
    //                         key: CACHE_KEYS.notificationSettings
    //                     }
    //                 }).subscribe(res => {
    //                     if (res.data) {
    //                         this._notificationSettings = JSON.parse(res.data);
    //                     } else {
    //                         // If there is no data then doing Default Notification Settings.
    //                         this._notificationSettings = {
    //                             notifications: true,
    //                             orderConfirmations: true,
    //                             alertNotifications: true,
    //                             broadcastNotifications: true
    //                         };
    //                     }
    //                     resolve(this._notificationSettings);
    //                 }, (err) => {
    //                     reject(err);
    //                 });
    //             });
    //             return this._notificationSettingsRequest;
    //         }
    //     } else {
    //         return new Promise(resolve => resolve(this._notificationSettings));
    //     }
    // }


    setNotificationSettings(notificationSettingsRequest: SetCacheRequestParams) {
        const url = applicationUrls.setCacheUrl;
        this._notificationSettings = JSON.parse(notificationSettingsRequest.data);
        return this._http.post(url, notificationSettingsRequest);
    }

    getTableColumns(tableName: string) {
        const url = applicationUrls.getCacheUrl;
        if (this._currentTable.has(tableName)) {
            return Promise.resolve(this._currentTable.get(tableName));
        } else {
            if (this._currentTableRequest.has(tableName)) {
                return this._currentTableRequest.get(tableName);
            } else {
                this._currentTableRequest.set(tableName, new Promise((resolve, reject) => this._http.get<any>(url, {
                    params: {
                        key: tableName
                    }
                }).subscribe(res => {
                    if (res) {
                        this._currentTable.set(tableName, res.data);
                       resolve(this._currentTable.get(tableName));
                    }
                    return resolve(null);
                },(error) => {
                    reject();
                })));
                return this._currentTableRequest.get(tableName);
            }
        }
    }

    setTableColumns(setTableColumnsParams: SetCacheRequestParams) {
        const url = applicationUrls.setCacheUrl;
        return this._http.post<any>(url, setTableColumnsParams);
    }

    getChartLibrary(key: string) {
        const url = applicationUrls.getCacheUrl;

        if (this._chartLibrary === undefined) {
            return this._http.get<any>(url, {
                params: {
                    key: key
                }
            }).map(res => {
                if (res.data) {
                    this._chartLibrary = res.data;
                    return this._chartLibrary;
                }
                this._chartLibrary = null;
                return null;
            });
        } else {
            return of(this._chartLibrary);
        }
    }

    setChartLibrary(setChartLibraryParams: SetCacheRequestParams) {
        const url = applicationUrls.setCacheUrl;
        this._chartLibrary = setChartLibraryParams.data;
        return this._http.post<any>(url, setChartLibraryParams);
    }

    setMarketWatchSettings(settings: MarketWatchSettings) {
        const url = applicationUrls.setCacheUrl;
        this._marketWatchSettings = settings;
        const request: SetCacheRequestParams = {
            key: CACHE_KEYS.marketWatchSettings,
            data: JSON.stringify(settings)
        };
        return this._http.post<any>(url, request);
    }

    getMarketWatchSettings(): Promise<MarketWatchSettings> {
        if (this._marketWatchSettings === undefined) {
            if (this._marketWatchSettingsRequest) {
                return this._marketWatchSettingsRequest;
            } else {
                const url = applicationUrls.getCacheUrl;
                this._marketWatchSettingsRequest = new Promise((resolve, reject) => {
                    this._http.get<CacheResponse>(url, {
                        params: {
                            key: CACHE_KEYS.marketWatchSettings
                        }
                    }).subscribe(res => {
                        if (res.data) {
                            this._marketWatchSettings = JSON.parse(res.data);
                        } else {
                            this._marketWatchSettings = null;
                        }
                        resolve(this._marketWatchSettings);
                    }, (err) => {
                        reject(err);
                    });
                });
                return this._marketWatchSettingsRequest;
            }
        } else {
            return new Promise(resolve => resolve(this._marketWatchSettings));
        }
    }

    getBrokerDetails(): Promise<BrokerDetails> {
        if (this._brokerDetails === undefined) {
            if (this._brokerDetailsRequest) {
                return this._brokerDetailsRequest;
            } else {
                const url = applicationUrls.getBrokerDetails;
                this._brokerDetailsRequest = new Promise((resolve, reject) => {
                    this._http.get<BrokerDetails>(url).subscribe(res => {
                        this._brokerDetails = res;
                        resolve(res);
                    }, err => {
                        reject(err);
                    });
                });

                return this._brokerDetailsRequest;
            }
        } else {
            return Promise.resolve(this._brokerDetails);
        }
    }

    setDefaultMarketWatch(id: number) {
        const url = applicationUrls.setCacheUrl;
        const request: SetCacheRequestParams = {
            key: CACHE_KEYS.defaultMarketWatch,
            data: JSON.stringify(id)
        };
        return this._http.post<any>(url, request);
    }

    getDefaultMarketWatch(): Promise<DefaultmarketWatch> {
        if (this._defaultMarketWatch === undefined) {
            if (this._defaultMarketWatchRequest) {
                return this._defaultMarketWatchRequest;
            } else {
                const url = applicationUrls.getCacheUrl;
                this._defaultMarketWatchRequest = new Promise((resolve, reject) => {
                    this._http.get<CacheResponse>(url, {
                        params: {
                            key: CACHE_KEYS.defaultMarketWatch
                        }
                    }).subscribe(res => {
                        if (res.data) {
                            this._defaultMarketWatch = JSON.parse(res.data);
                        } else {
                            this._defaultMarketWatch = null;
                        }
                        resolve(this._defaultMarketWatch);
                    }, (err) => {
                        reject(err);
                    });
                });
                return this._defaultMarketWatchRequest;
            }
        } else {
            return new Promise(resolve => resolve(this._defaultMarketWatch));
        }
    }

    getUserTheme() {
        const url = applicationUrls.getCacheUrl;

        if (this._currentTheme === undefined) {
            return this._http.get<CacheResponse>(url, {
                params: {
                    key: CACHE_KEYS.currentTheme
                }
            }).map(res => {
                if (res.data) {
                    this._currentTheme = res.data;
                    return this._currentTheme;
                }
                this._currentTheme = null;
                return null;
            });
        } else {
            return of(this._currentTheme);
        }
    }


    setUserTheme(currentThemeRequest: SetCacheRequestParams) {
        const url = applicationUrls.setCacheUrl;
        this._currentTheme = currentThemeRequest.data;
        return this._http.post(url, currentThemeRequest);
    }

    getPinnedSymbol() {
        const url = applicationUrls.getCacheUrl;
        return this._http.get<CacheResponse>(url, {
            params: {
                key: CACHE__KEYS.pinnedSymbol
            }
        }).map(res => {
            if (res.data) {
                return JSON.parse(res.data);
            }
            return DEFAULT_PINNED_SYMBOLS_OBJ;
        });
    }

    setPinnedSymbol(indices) {
        const url = applicationUrls.setCacheUrl;
        return this._http.post(url, {
            key: CACHE__KEYS.pinnedSymbol,
            data: JSON.stringify(indices)
        });
    }

    setUPIId(UPIId: string, key?: string) {
        this._UPIId = null;
        const url = applicationUrls.setCacheUrl;
        const cacheKey = key || CACHE_KEYS.UPIId;
        return this._http.post(url, {
            key: cacheKey,
            data: JSON.stringify(UPIId)
        });

    }

    getUPIId(key?: string) {
        const url = applicationUrls.getCacheUrl;
        const cacheKey = key || CACHE_KEYS.UPIId;
        if (!(!!this._UPIId)) {
            return this._http.get<CacheResponse>(url, {
                params: {
                    key: cacheKey
                }
            }).map(res => {
                if (res.data) {
                    this._UPIId = res.data;
                    return this._UPIId;
                }
            });
        } else {
            return of(this._UPIId);
        }
    }

}
